import React from "react";
import { Pill, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AddMedicationButton } from "./DashboardMedicationsModal";
import { Medication } from "@/lib/dashboard/medicationsData";
import { useMedicationsState } from "@/lib/dashboard/medicationsData";
import Skeleton from "@mui/material/Skeleton";
import { getVisiblePages } from "@/lib/utils";

interface CurrentMedicationsProps {
  medications: Medication[];
  isDarkMode: boolean;
  onSaveMedication: (medicationData: any) => void;
  onEditMedication: (id: string, medicationData: any) => void;
}

export function CurrentMedications({
  medications,
  isDarkMode,
  onSaveMedication,
  onEditMedication,
}: CurrentMedicationsProps) {
  const {
    patientMedicationsLoading,
    paginatedMedications,
    totalPages,
    currentPage,
    itemsPerPage,
    handlePageChange,

  } = useMedicationsState();

  return (
    <div
      className={`rounded-xl p-4 transition-colors duration-300 ${isDarkMode ? "bg-gray-800" : "bg-white"
        } shadow-sm`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Pill
            className={`w-4 h-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          />
          <h3
            className={`text-sm font-semibold transition-colors duration-300 ${isDarkMode ? "text-white" : "text-gray-900"
              }`}
          >
            Current Medications
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className={`text-xs ${isDarkMode
                ? "bg-gray-700 text-gray-300"
                : "bg-gray-100 text-gray-600"
              }`}
          >
            {patientMedicationsLoading ? "..." : medications.length}
          </Badge>
          <AddMedicationButton
            onSave={onSaveMedication}
            isDarkMode={isDarkMode}
          />
        </div>
      </div>

      <div className="space-y-3">
        {patientMedicationsLoading ? (
          // 🔹 Skeleton placeholders
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className={`border rounded-lg p-3 ${isDarkMode ? "border-gray-600" : "border-gray-200"
                }`}
            >
              <Skeleton
                variant="text"
                width="60%"
                height={20}
                sx={{ bgcolor: isDarkMode ? "grey.700" : "grey.300" }}
              />
              <Skeleton
                variant="text"
                width="40%"
                height={16}
                sx={{ bgcolor: isDarkMode ? "grey.700" : "grey.300" }}
              />
              <Skeleton
                variant="text"
                width="80%"
                height={16}
                sx={{ bgcolor: isDarkMode ? "grey.700" : "grey.300" }}
              />
            </div>
          ))
        ) : medications.length === 0 ? (
          // 🔹 Empty state
          <div
            className={`text-center py-6 border rounded-lg transition-colors duration-300 ${isDarkMode
                ? "bg-gray-700 border-gray-600 text-gray-400"
                : "bg-gray-50 border-gray-200 text-gray-500"
              }`}
          >
            <Pill className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-xs">No medications recorded</p>
            <p className="text-xs mt-1 opacity-75">
              Click the + button to add medications
            </p>
          </div>
        ) : (
          // 🔹 Real data with pagination
          paginatedMedications.map((medication) => (
            <AddMedicationButton
              key={medication.id}
              editMode={{
                id: medication.id,
                item: medication.item,
                dose: medication.dose,
                times: medication.times,
                quantity: medication.quantity,
                measure: medication.measure,
                therapyLevel: medication.therapyLevel,
                preparation: medication.preparation,
                notes: medication.notes,
              }}
              onSave={(data) => onEditMedication(medication.id, data)}
              trigger={
                <div
                  className={`border rounded-lg p-3 cursor-pointer group transition-colors duration-300 ${isDarkMode
                      ? "border-gray-600 hover:bg-gray-700"
                      : "border-gray-200 hover:bg-gray-50"
                    }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-xs font-medium transition-colors duration-300 ${isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      {medication.item}
                    </span>
                    {(() => {
                      const therapyMap: Record<string, string> = {
                        "1": "Chronic",
                        "2": "Acute",
                        "3": "Maintenance",
                        "4": "As Needed",
                      };
                      const levelKey = String(medication.therapyLevel ?? "");
                      const levelLabel =
                        therapyMap[levelKey] ?? medication.therapyLevel ?? "-";

                      let levelClasses = isDarkMode
                        ? "bg-blue-900/30 text-blue-300 border border-blue-700"
                        : "bg-blue-100 text-blue-700";

                      if (levelKey === "Chronic") {
                        levelClasses = isDarkMode
                          ? "bg-sky-900/30 text-sky-300 border border-sky-700"
                          : "bg-[#ffedd5] text-[#c2410c]";
                      } else if (levelKey === "Acute") {
                        levelClasses = isDarkMode
                          ? "bg-orange-900/30 text-orange-300 border border-orange-700"
                          : "bg-[#fee2e2] text-[#b91c1c]";
                      } else if (levelKey === "Maintenance") {
                        levelClasses = isDarkMode
                          ? "bg-violet-900/30 text-violet-300 border border-violet-700"
                          : "bg-[#fef3c7] text-[#d97706]";
                      } else if (levelKey === "AsNeeded") {
                        levelClasses = isDarkMode
                          ? "bg-red-900/30 text-red-300 border border-red-700"
                          : "bg-[#dcfce7] text-[#829079]";
                      }

                      return (
                        <Badge
                          variant="secondary"
                          className={`text-xs ${levelClasses}`}
                        >
                          {levelLabel == "AsNeeded" ? "As Needed" : levelLabel}
                        </Badge>
                      );
                    })()}
                  </div>
                  <p
                    className={`text-xs transition-colors duration-300 ${isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                  >
                    {medication.dose}
                  </p>
                </div>
              }
              isDarkMode={isDarkMode}
            />
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {!patientMedicationsLoading &&
        medications.length > itemsPerPage &&
        totalPages > 1 && (
          <div
            className={`flex items-center justify-between mt-4 pt-3 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
          >
            <div className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, medications.length)} of {medications.length}
            </div>

            <div className="flex items-center gap-1 flex-wrap max-w-full">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-1 rounded transition-colors duration-300 ${currentPage === 1
                    ? isDarkMode
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gray-400 cursor-not-allowed"
                    : isDarkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {(() => {
                const { start, end } = getVisiblePages(currentPage, totalPages, 5); // max 5 visible

                return (
                  <>
                    {start > 1 && (
                      <>
                        <button
                          onClick={() => handlePageChange(1)}
                          className="px-2 py-1 text-xs rounded text-gray-600 hover:bg-gray-100"
                        >
                          1
                        </button>
                        <span className="px-1 text-xs text-gray-400">…</span>
                      </>
                    )}

                    {Array.from({ length: end - start + 1 }, (_, i) => start + i).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-2 py-1 text-xs rounded transition-colors ${page === currentPage
                            ? isDarkMode
                              ? "bg-blue-600 text-white"
                              : "bg-blue-500 text-white"
                            : isDarkMode
                              ? "text-gray-300 hover:bg-gray-700"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                      >
                        {page}
                      </button>
                    ))}

                    {end < totalPages && (
                      <>
                        <span className="px-1 text-xs text-gray-400">…</span>
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          className="px-2 py-1 text-xs rounded text-gray-600 hover:bg-gray-100"
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </>
                );
              })()}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-1 rounded transition-colors duration-300 ${currentPage === totalPages
                    ? isDarkMode
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gray-400 cursor-not-allowed"
                    : isDarkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

    </div>
  );
}
